import { Box, Card, Stack, Typography } from "@mui/material";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import CardAccountDetailsIcon from "@entrilia/shared/icons/CardAccountDetailsIcon";
import type {
  InvestorCommunicationEngagementSummary,
  InvestorCommunicationType,
} from "../../../../../../api/types/investorCommunicationsTypes";
import { investorCommunicationEngagementLabels } from "./investorCommunicationEngagementLabels";

interface Props {
  type: InvestorCommunicationType;
  summary: InvestorCommunicationEngagementSummary;
}

interface Segment {
  label: string;
  count: number;
  color: string;
}

const getSegments = (type: InvestorCommunicationType, summary: InvestorCommunicationEngagementSummary): Segment[] => [
  {
    label: investorCommunicationEngagementLabels[type].completedLabel,
    count: summary.readFullyCount,
    color: "success.main",
  },
  {
    label: investorCommunicationEngagementLabels[type].openedNotCompletedLabel,
    count: summary.openedNotReadFullyCount,
    color: "success.light",
  },
  { label: "Not opened", count: summary.notOpenedCount, color: "action.disabledBackground" },
];

const swatchSx = { width: 8, height: 8, borderRadius: 1 };

const InvestorCommunicationReadBreakdown = ({ type, summary }: Props) => {
  const { recipientsCount, formerRecipientReadersCount } = summary;
  const segments = getSegments(type, summary);
  const getPercentage = (count: number) => (recipientsCount === 0 ? 0 : Math.round((count * 100) / recipientsCount));

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CardAccountDetailsIcon color="action" />
            <Typography variant="subtitle2">Portal</Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary">
            all {recipientsCount} {pluralize("recipient", recipientsCount)}
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.25}>
          {recipientsCount === 0 ? (
            <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: "action.disabledBackground" }} />
          ) : (
            segments
              .filter((segment) => segment.count > 0)
              .map((segment) => (
                <Box
                  key={segment.label}
                  sx={{ flex: segment.count, height: 8, borderRadius: 1, bgcolor: segment.color }}
                />
              ))
          )}
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Stack direction="row" spacing={3}>
            {segments.map((segment) => (
              <Stack key={segment.label} direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Box sx={{ ...swatchSx, bgcolor: segment.color }} />
                <Typography variant="caption" color="textSecondary">
                  {segment.label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                  {segment.count}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {getPercentage(segment.count)}%
                </Typography>
              </Stack>
            ))}
          </Stack>
          {formerRecipientReadersCount > 0 && (
            <Typography variant="caption" color="textSecondary">
              Also viewed by {formerRecipientReadersCount} former {pluralize("recipient", formerRecipientReadersCount)}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
};

export default InvestorCommunicationReadBreakdown;
